/**
 * Operational Tools Dashboard
 *
 * Access operational tools for question quality management:
 * - Deduplication: Find and manage duplicate questions
 * - Performance Summary: Track question performance metrics
 * - Change Detection: Detect product catalog changes
 *
 * Date: 2025-11-15
 */

import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Copy,
  TrendingUp,
  RefreshCw,
  Loader2,
  AlertCircle,
  CheckCircle2,
  Search,
} from 'lucide-react';
import { authFetch } from '@/utils/auth-fetch';
import { useToast } from '@/components/ui/use-toast';

export default function OperationalToolsDashboard() {
  const { sectorId } = useParams<{ sectorId: string }>();
  const { toast } = useToast();

  // Deduplication state
  const [dedupLoading, setDedupLoading] = useState(false);
  const [dedupThreshold, setDedupThreshold] = useState(0.9);
  const [dedupResults, setDedupResults] = useState<any[]>([]);

  // Performance state
  const [perfLoading, setPerfLoading] = useState(false);
  const [perfLimit, setPerfLimit] = useState(20);
  const [perfResults, setPerfResults] = useState<any[]>([]);

  // Change detection state
  const [changeLoading, setChangeLoading] = useState(false);
  const [changeResults, setChangeResults] = useState<any>(null);

  const findDuplicates = async () => {
    try {
      setDedupLoading(true);
      const response = await authFetch(
        `/api/sectors/${sectorId}/find-duplicate-questions?similarity_threshold=${dedupThreshold}`
      );
      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to find duplicates');
      }

      setDedupResults(data.duplicateGroups || []);

      toast({
        title: 'Deduplication Complete',
        description: `Found ${data.duplicateGroups?.length || 0} duplicate groups`,
      });
    } catch (error: any) {
      console.error('Error finding duplicates:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to find duplicates',
        variant: 'destructive',
      });
    } finally {
      setDedupLoading(false);
    }
  };

  const loadPerformanceSummary = async () => {
    try {
      setPerfLoading(true);
      const response = await authFetch(
        `/api/sectors/${sectorId}/question-performance-summary?limit=${perfLimit}`
      );
      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to load performance summary');
      }

      setPerfResults(data.questions || []);

      toast({
        title: 'Performance Summary Loaded',
        description: `Loaded ${data.questions?.length || 0} questions`,
      });
    } catch (error: any) {
      console.error('Error loading performance:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to load performance summary',
        variant: 'destructive',
      });
    } finally {
      setPerfLoading(false);
    }
  };

  const detectChanges = async () => {
    try {
      setChangeLoading(true);
      const response = await authFetch(
        `/api/sectors/${sectorId}/detect-product-changes`
      );
      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to detect changes');
      }

      setChangeResults(data);

      toast({
        title: 'Change Detection Complete',
        description: `Analyzed product catalog changes`,
      });
    } catch (error: any) {
      console.error('Error detecting changes:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to detect changes',
        variant: 'destructive',
      });
    } finally {
      setChangeLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Operational Tools</h1>
        <p className="text-muted-foreground mt-2">
          Manage question quality, performance, and product catalog changes
        </p>
      </div>

      <Tabs defaultValue="deduplication" className="space-y-4">
        <TabsList>
          <TabsTrigger value="deduplication">
            <Copy className="h-4 w-4 mr-2" />
            Deduplication
          </TabsTrigger>
          <TabsTrigger value="performance">
            <TrendingUp className="h-4 w-4 mr-2" />
            Performance
          </TabsTrigger>
          <TabsTrigger value="changes">
            <RefreshCw className="h-4 w-4 mr-2" />
            Change Detection
          </TabsTrigger>
        </TabsList>

        {/* Deduplication Tab */}
        <TabsContent value="deduplication">
          <Card>
            <CardHeader>
              <CardTitle>Find Duplicate Questions</CardTitle>
              <CardDescription>
                Identify similar or duplicate questions across wheels
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-end gap-4">
                <div className="flex-1">
                  <Label htmlFor="threshold">Similarity Threshold</Label>
                  <Input
                    id="threshold"
                    type="number"
                    min="0.5"
                    max="1"
                    step="0.05"
                    value={dedupThreshold}
                    onChange={(e) => setDedupThreshold(parseFloat(e.target.value))}
                  />
                  <p className="text-sm text-muted-foreground mt-1">
                    Higher values = stricter matching (0.5 - 1.0)
                  </p>
                </div>
                <Button onClick={findDuplicates} disabled={dedupLoading}>
                  {dedupLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Searching...
                    </>
                  ) : (
                    <>
                      <Search className="mr-2 h-4 w-4" />
                      Find Duplicates
                    </>
                  )}
                </Button>
              </div>

              {dedupResults.length > 0 && (
                <div className="space-y-4">
                  <Alert>
                    <CheckCircle2 className="h-4 w-4" />
                    <AlertDescription>
                      Found {dedupResults.length} groups of duplicate questions
                    </AlertDescription>
                  </Alert>

                  <div className="space-y-3">
                    {dedupResults.map((group, idx) => (
                      <Card key={idx}>
                        <CardHeader>
                          <CardTitle className="text-base">
                            Duplicate Group {idx + 1}
                          </CardTitle>
                          <CardDescription>
                            {group.questions?.length || 0} similar questions (similarity:{' '}
                            {(group.similarity * 100).toFixed(1)}%)
                          </CardDescription>
                        </CardHeader>
                        <CardContent>
                          <ul className="space-y-2">
                            {group.questions?.map((q: any, qIdx: number) => (
                              <li key={qIdx} className="text-sm">
                                <Badge variant="outline" className="mr-2">
                                  {q.wheel_name}
                                </Badge>
                                {q.question}
                              </li>
                            ))}
                          </ul>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}

              {dedupResults.length === 0 && !dedupLoading && (
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    Click "Find Duplicates" to search for similar questions
                  </AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Performance Tab */}
        <TabsContent value="performance">
          <Card>
            <CardHeader>
              <CardTitle>Question Performance Summary</CardTitle>
              <CardDescription>
                Track views, clicks, and engagement metrics
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-end gap-4">
                <div className="flex-1">
                  <Label htmlFor="limit">Number of Questions</Label>
                  <Input
                    id="limit"
                    type="number"
                    min="5"
                    max="100"
                    value={perfLimit}
                    onChange={(e) => setPerfLimit(parseInt(e.target.value))}
                  />
                </div>
                <Button onClick={loadPerformanceSummary} disabled={perfLoading}>
                  {perfLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Loading...
                    </>
                  ) : (
                    <>
                      <TrendingUp className="mr-2 h-4 w-4" />
                      Load Summary
                    </>
                  )}
                </Button>
              </div>

              {perfResults.length > 0 && (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Question</TableHead>
                      <TableHead>Wheel</TableHead>
                      <TableHead className="text-right">Views</TableHead>
                      <TableHead className="text-right">Clicks</TableHead>
                      <TableHead className="text-right">CTR</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {perfResults.map((q: any, idx: number) => (
                      <TableRow key={idx}>
                        <TableCell className="font-medium">{q.question}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{q.wheel_name}</Badge>
                        </TableCell>
                        <TableCell className="text-right">{q.views || 0}</TableCell>
                        <TableCell className="text-right">{q.clicks || 0}</TableCell>
                        <TableCell className="text-right">
                          {q.views > 0
                            ? ((q.clicks / q.views) * 100).toFixed(1) + '%'
                            : '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}

              {perfResults.length === 0 && !perfLoading && (
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    Click "Load Summary" to view performance metrics
                  </AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Change Detection Tab */}
        <TabsContent value="changes">
          <Card>
            <CardHeader>
              <CardTitle>Product Catalog Change Detection</CardTitle>
              <CardDescription>
                Detect new, modified, or removed products
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Button onClick={detectChanges} disabled={changeLoading}>
                {changeLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Analyzing...
                  </>
                ) : (
                  <>
                    <RefreshCw className="mr-2 h-4 w-4" />
                    Detect Changes
                  </>
                )}
              </Button>

              {changeResults && (
                <div className="grid gap-4 md:grid-cols-3">
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-2xl text-green-600">
                        {changeResults.newProducts || 0}
                      </CardTitle>
                      <CardDescription>New Products</CardDescription>
                    </CardHeader>
                  </Card>
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-2xl text-blue-600">
                        {changeResults.modifiedProducts || 0}
                      </CardTitle>
                      <CardDescription>Modified Products</CardDescription>
                    </CardHeader>
                  </Card>
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-2xl text-red-600">
                        {changeResults.removedProducts || 0}
                      </CardTitle>
                      <CardDescription>Removed Products</CardDescription>
                    </CardHeader>
                  </Card>
                </div>
              )}

              {!changeResults && !changeLoading && (
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    Click "Detect Changes" to analyze product catalog
                  </AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
